/* eslint no-console: 0 */
import * as Cookie from 'cookie';

import type { IncomingMessage, ServerResponse } from 'node:http';

// Public API of the cookie plugin. Kept at module scope with an explicit
// return type on the factory so consumers (via AwCookiesPluginProvides) always
// resolve a concrete type — an inferred, body-local interface collapses to
// `unknown` in tsconfig scopes that read this package without its build types.
export interface CookieState {
  parseJSON: boolean;
  set: (name?: string, value?: string | number | boolean | object, opts?: Record<string, unknown>) => void;
  setAll: (cookieList?: Array<{ name?: string; value?: string; opts?: Record<string, unknown> }>) => void;
  get: (name?: string, opts?: { parseJSON?: boolean }) => string | unknown | number | boolean;
  getAll: (opts?: { parseJSON?: boolean }) => Record<string, unknown>;
  remove: (name?: string, opts?: Record<string, unknown>) => void;
  removeAll: () => void;
  nodeCookie: typeof Cookie;
}

export function awCookieUniversalFactory (
  {
    req,
    res,
    parseJSON = true,
    awIsSecureByDefault = false,
  }:
  {
    req: IncomingMessage | undefined,
    res: ServerResponse | undefined,
    parseJSON: boolean | undefined,
    awIsSecureByDefault: boolean | undefined,
  },
): CookieState {
  // NOTE: This is an edited version of 2.1.5:
  // https://raw.githubusercontent.com/microcipcip/cookie-universal/v2.1.5/packages/cookie-universal/index.js
  // Added awIsSecureByDefault so secure doesn't have to be set manually each time.
  // This version only supports secure on set, setAll, remove:
  // https://github.com/microcipcip/cookie-universal/tree/v2.1.5/packages/cookie-universal
  const isClient = (
    typeof document === 'object' && typeof document.cookie === 'string'
  );
  const isServer = (
    typeof req === 'object' && typeof res === 'object'
  );
  const isNeither = (!isClient && !isServer) || (isClient && isServer);
  if (isNeither) {
    console.error('awCookieUniversalFactory is not supported in this environment', 'isNeither', isNeither);
  }

  const getHeaders = () => {
    if (isServer) {
      // WARNING awOverwrite
      const requestCookie = req.headers.cookie || '';
      const setCookiesHeader = res?.getHeaders?.()?.['set-cookie'] || [];
      const responseCookiesArr = (Array.isArray(setCookiesHeader) ? setCookiesHeader : [setCookiesHeader]).map(c => String(c).split(';')[0] || '') || [];
      const requestCookiesArr = requestCookie.split(';') || [];
      const headersArr = requestCookiesArr.concat(responseCookiesArr);
      const map: { [key: string]: string } = {};
      headersArr.forEach(item => {
        const [key, value] = item.trim().split('=');
        if (key) {
          map[key] = value || '';
        }
      });
      return Object.keys(map).map(key => `${key}=${map[key]}`).join(';');
    }
    if (isClient) {
      return document.cookie || '';
    }
  };

  const getResponseCookies = () => {
    if (!res) {
      return [];
    }
    const cookies = res.getHeader('Set-Cookie');
    if (Array.isArray(cookies)) {
      return cookies;
    } else if (typeof cookies === 'string') {
      return [cookies];
    }
    return [];
  };
  const setResponseCookie = (cookieList: unknown[]) => {
    if (!res) {
      return;
    }
    // WARNING awOverwrite
    const map: { [key: string]: { value: string, attributes: string } } = {};

    // Iterate over the input array
    cookieList.forEach((item) => {
      const [keyValue, ...attributes] = `${item}`.split('; '); // Split into key-value pair and attributes
      if (keyValue) {
        const [key, value] = keyValue.split('='); // Split key and value

        // If the key already exists, update the value
        // Otherwise, add a new entry
        if (key) {
          map[key] = { value: value || '', attributes: attributes.join('; ') };
        }
      }
    });

    // Convert the map back into an array
    const uniqueArray = Object.keys(map).map(key => `${key}=${map[key]!.value}; ${map[key]!.attributes}`);
    return res.setHeader('Set-Cookie', uniqueArray);
  };

  const parseToJSON = (val: string, enableParsing: boolean) => {
    if (!enableParsing) {
      return val;
    }
    try {
      return JSON.parse(val);
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (err) {
      return val;
    }
  };


  // public api
  const state: CookieState = {
    parseJSON,
    set (name = '', value = '', opts = { path: '/' }) {
      if (isNeither) {
        return;
      }
      value = typeof value === 'object' ? JSON.stringify(value) : value;
      // WARNING awOverwrite
      const awOpts = {
        secure: awIsSecureByDefault,
        ...opts,
      };

      if (isServer) {
        const cookies = getResponseCookies();
        cookies.push(Cookie.serialize(name, String(value), awOpts));
        setResponseCookie(cookies);
      } else {
        document.cookie = Cookie.serialize(name, String(value), awOpts);
      }
    },

    setAll (cookieList = []) {
      if (isNeither) {
        return;
      }
      if (!Array.isArray(cookieList)) {
        return;
      }
      cookieList.forEach((cookie) => {
        const { name = '', value = '', opts = { path: '/' } } = cookie;
        // WARNING awOverwrite
        const awOpts = {
          secure: awIsSecureByDefault,
          ...opts,
        };
        state.set(name, value, awOpts);
      });
    },

    get (name = '', opts = { parseJSON: state.parseJSON }) {

      if (isNeither) {
        return '';
      }

      const cookies = Cookie.parse(getHeaders() || '');
      const cookie = cookies[name];
      return parseToJSON(cookie || '', opts.parseJSON ?? parseJSON);
    },

    getAll (opts = { parseJSON: state.parseJSON }) {
      if (isNeither) {
        return {};
      }
      const cookies = Cookie.parse(getHeaders() || '');
      for (const cookie in cookies) {
        const value = cookies[cookie];
        cookies[cookie] = parseToJSON(value || '', opts.parseJSON ?? parseJSON);
      }
      return cookies;
    },

    remove (name = '', opts = { path: '/' }) {
      if (isNeither) {
        return;
      }
      const cookie = state.get(name);
      if (typeof cookie !== 'undefined') {
        // WARNING awOverwrite
        const awOpts = {
          secure: awIsSecureByDefault,
          ...opts,
          expires: new Date(0),
        };
        state.set(name, '', awOpts);
      }
    },

    removeAll () {
      if (isNeither) {
        return;
      }
      const cookies = Cookie.parse(getHeaders() || '');
      for (const cookie in cookies) {
        state.remove(cookie);
      }
    },

    // expose cookie library
    nodeCookie: Cookie,
  };

  return state;
}
